import { config } from "~/data/config";
import { Repo } from "../apis/repo";
import { RequestData } from "../apis/repo_base";
import { FetchOptions } from "~/types";
import {
  InitializeCommsBatchRequest,
  CommsBatchResponse,
  CommsBatchEntriesResponse,
  CommsBatchEntriesParams,
  UpdateCommsTemplateRequest,
  CommsTemplateResponse,
  CommsTemplatesResponse,
  UpdateCommsChannelsRequest,
  CommsChannelsResponse,
  MapCommsRecipientsRequest,
  MapCommsRecipientsResponse,
  CommsCsvUploadResponse,
  SendCommsBatchResponse,
  CommsBatchStatusResponse,
  BatchValidationResponse,
  CommsBatchEntry,
  AssetMetadata,
  CommunicationChannel,
  DeliveryStatus,
  ChannelStatus,
  ResetBatchRequest,
  ResetBatchResponse,
  BatchDetailsResponse,
  CommsEntryByAssetResponse,
  CommsBatchesListParams,
  CommsBatchesListResponse,
} from "../types/comms";

const defaultMockDelayPromise = (ms: number = 1500) =>
  new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, ms);
  });

// Mock data generators
const generateMockBatchId = () =>
  `BATCH_${Date.now()}_${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
const generateMockJobId = () =>
  `JOB_${Date.now()}_${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
const generateMockAssetId = () =>
  `ASSET_${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
const generateMockEntryId = () =>
  `ENTRY_${Math.random().toString(36).substr(2, 9).toUpperCase()}`;

const generateMockAssetMetadata = (index: number): AssetMetadata => {
  const brands = ["Amazon", "Flipkart", "Swiggy", "Zomato", "Myntra"];
  const brand = brands[index % brands.length];
  const amounts = [500, 1000, 1500, 2000, 250];

  return {
    type: "VOUCHER",
    amount: amounts[index % amounts.length],
    brandName: brand,
    brandLogoUrl: `https://app.myhubble.money/static/brand-assets/${brand.toLowerCase()}/top_brand.png`,
    termsAndConditions: [
      `Valid for 1 year from date of issue`,
      `Cannot be clubbed with other offers`,
    ],
    howToUseInstructions: [
      `Visit ${brand} website or app`,
      `Apply at checkout`,
    ],
  };
};

const generateMockChannelStatus = (channels: CommunicationChannel[]) => {
  const channelStatus: Record<CommunicationChannel, any> = {} as any;

  channels.forEach((channel, index) => {
    const statuses: ChannelStatus[] = ["SENT", "FAILED", "DELIVERED"];
    const status = statuses[index % statuses.length];

    channelStatus[channel] = {
      status,
      error: status === "FAILED" ? "Phone number not on WhatsApp" : null,
      attemptedAt: new Date(Date.now() - Math.random() * 3600000).toISOString(),
    };
  });

  return channelStatus;
};

const generateMockBatchEntry = (
  index: number,
  channels: CommunicationChannel[]
): CommsBatchEntry => {
  const names = [
    "John",
    "Jane Smith",
    "Robert Johnson",
    "Emily Davis Doe",
    "Michael Wilson",
  ];
  const emails = [
    "johnjohnjohnjohnjohnjohn@example.com",
    "emple.com",
    "robert@example.com",
    "emily@example.com",
    "michael@example.com",
  ];
  const phones = [
    "9876543210",
    "9876543211",
    "9876543212",
    "9876543213",
    "9876543214",
  ];
  const messages = [
    "Ha!",
    "Congratulations on your achievement!",
    "Thank you for your service! for your service! for your service!",
    "Enjoy your reward!",
    "Best wishes!",
  ];

  const deliveryStatuses: DeliveryStatus[] = ["SENT", "DELIVERED", "FAILED"];

  // if (false) {
  if (index > 1) {
    return {
      entryId: generateMockEntryId(),
      assetId: generateMockAssetId(),
      displayId: `VOUCHER_${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
      sequenceNumber: index + 1,
      metadata: generateMockAssetMetadata(index),
      recipientName: "",
      recipientEmail: "",
      recipientPhone: "",
      personalizedMessage: "",
      // recipientName: names[index % names.length],
      // recipientEmail: emails[index % emails.length],
      // recipientPhone: phones[index % phones.length],
      // personalizedMessage: messages[index % messages.length],
      deliveryStatus: deliveryStatuses[index % deliveryStatuses.length],
      channelStatus: generateMockChannelStatus(channels),
      sentAt: new Date(Date.now() - Math.random() * 86400000).toISOString(),
    };
  }

  return {
    entryId: generateMockEntryId(),
    assetId: generateMockAssetId(),
    displayId: `VOUCHER_${Math.random().toString(36).substr(2, 6).toUpperCase()}`,
    sequenceNumber: index + 1,
    metadata: generateMockAssetMetadata(index),
    // recipientName: "",
    // recipientEmail: "",
    // recipientPhone: "",
    // personalizedMessage: "",
    recipientName: names[index % names.length],
    recipientEmail: emails[index % emails.length],
    recipientPhone: phones[index % phones.length],
    personalizedMessage: messages[index % messages.length],
    deliveryStatus: deliveryStatuses[index % deliveryStatuses.length],
    channelStatus: generateMockChannelStatus(channels),
    sentAt: new Date(Date.now() - Math.random() * 86400000).toISOString(),
  };
};

export class CommsMockRepo extends Repo {
  constructor(fetch: <T>(request: RequestData) => Promise<T>) {
    super(fetch);
  }

  async initialiseCommsBatch(
    body: InitializeCommsBatchRequest,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsBatchResponse> {
    await defaultMockDelayPromise(800);

    return {
      batchId: generateMockBatchId(),
      sourceReferenceId: body.sourceReferenceId,
      assetType: body.assetType,
      totalAssets: Math.floor(Math.random() * 1000) + 100, // Random between 100-1100
      channels: body.channels,
      status: "DRAFT",
    };
  }

  async getCommsBatchEntries(
    batchId: string,
    params?: CommsBatchEntriesParams,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsBatchEntriesResponse> {
    await defaultMockDelayPromise(1000);

    const pageNo = params?.pageNo || 0;
    const limit = Math.min(params?.limit || 20, 100);
    const totalEntries = 400; // Mock total entries
    const startIndex = pageNo * limit;

    // Mock channels based on batch (you could store this in a map in real implementation)
    const channels: CommunicationChannel[] = ["EMAIL", "WHATSAPP", "SMS"];

    const mockEntries: CommsBatchEntry[] = [];
    for (let i = 0; i < Math.min(limit, totalEntries - startIndex); i++) {
      mockEntries.push(generateMockBatchEntry(startIndex + i, channels));
    }

    return {
      data: mockEntries,
      nextCursor: {
        pageNo,
        limit,
        totalElements: totalEntries,
      },
    };
  }

  async updateCommsMessageTemplate(
    batchId: string,
    body: UpdateCommsTemplateRequest,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsTemplateResponse> {
    await defaultMockDelayPromise(600);

    return {
      batchId,
      subject: body.subject,
      companyLogoUrl: body.companyLogoUrl,
      messageTitle: body.messageTitle,
      genericMessage: body.genericMessage,
      imageUrl: body.imageUrl,
    };
  }

  async updateCommsChannels(
    batchId: string,
    body: UpdateCommsChannelsRequest,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsChannelsResponse> {
    await defaultMockDelayPromise(500);

    return {
      batchId,
      channels: body.channels,
      status: "DRAFT",
    };
  }

  async getCommsChannels(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsChannelsResponse> {
    await defaultMockDelayPromise(300);

    return {
      batchId,
      channels: ["EMAIL", "SMS", "WHATSAPP"],
      status: "DRAFT",
    };
  }

  async mapCommsRecipients(
    batchId: string,
    body: MapCommsRecipientsRequest,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<MapCommsRecipientsResponse> {
    await defaultMockDelayPromise(1200);

    // Simulate some successful updates and some errors
    const totalItems = body.data.length;
    const errorCount = Math.floor(totalItems * 0.1); // 10% error rate
    const updatedCount = totalItems - errorCount;

    const errors = [];
    for (let i = 0; i < errorCount; i++) {
      errors.push({
        assetId: body.data[i]?.assetId || "UNKNOWN_ASSET",
        error:
          i % 2 === 0
            ? "Asset not found in batch"
            : "Invalid recipient email format",
      });
    }

    return {
      updated: updatedCount,
      errors,
    };
  }

  async downloadCommsCsvTemplate(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<any> {
    await defaultMockDelayPromise(800);

    // Mock CSV content
    const csvContent = `assetId,amount,brandName,recipientName,recipientEmail,recipientPhone,personalizedMessage
ASSET_001,500,Amazon,,,,
ASSET_002,1000,Flipkart,,,,
ASSET_003,250,Swiggy,,,,
ASSET_004,750,Myntra,,,,
ASSET_005,2000,Zomato,,,,`;

    return csvContent;
  }

  async uploadCommsCsvRecipients(
    batchId: string,
    file: File,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsCsvUploadResponse> {
    await defaultMockDelayPromise(2000); // Longer delay for file upload

    // Simulate processing results
    const rowsProcessed = Math.floor(Math.random() * 1000) + 100;
    const errorCount = Math.floor(rowsProcessed * 0.05); // 5% error rate
    const rowsUpdated = rowsProcessed - errorCount;

    // Generate mock mapping errors
    const mappingErrors = [];
    for (let i = 0; i < Math.min(3, errorCount); i++) {
      mappingErrors.push({
        assetId: generateMockAssetId(),
        error:
          i % 2 === 0 ? "Invalid email format" : "Asset not found in batch",
      });
    }

    const response: CommsCsvUploadResponse = {
      success: true,
      rowsProcessed,
      rowsWithErrors: errorCount,
      errorCsvData:
        errorCount > 0 ? "assetId,error\nASSET_001,Invalid email format" : null,
      mappingResult: {
        updated: rowsUpdated,
        errors: mappingErrors,
      },
    };

    return response;
  }

  async validateCommsRecipientMappings(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<BatchValidationResponse> {
    await defaultMockDelayPromise(800);

    const totalAssets = Math.floor(Math.random() * 1000) + 100;
    const mappedAssets = Math.floor(totalAssets * 0.85); // 85% mapped
    const unmappedAssets = totalAssets - mappedAssets;

    const missingName = Math.floor(unmappedAssets * 0.3);
    const missingEmail = Math.floor(unmappedAssets * 0.4);
    const missingPhone = Math.floor(unmappedAssets * 0.3);

    const canSend = unmappedAssets === 0;

    return {
      canSend: true,
      totalAssets,
      mappedAssets,
      unmappedAssets,
      validationSummary: {
        missingName,
        missingEmail,
        missingPhone,
      },
    };
  }

  async getCommsMessageTemplate(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsTemplateResponse> {
    await defaultMockDelayPromise(500);

    return {
      batchId,
      subject: "Your Special Gift is Here!",
      companyLogoUrl: "https://cdn.example.com/company-logo.png",
      messageTitle: "Happy Celebrations!",
      genericMessage:
        "We're excited to share this special gift with you. Enjoy your rewards!",
      imageUrl: "https://cdn.example.com/celebration-banner.jpg",
    };
  }

  async getCommsTemplates(
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsTemplatesResponse> {
    await defaultMockDelayPromise(800);

    return {
      templates: [
        {
          templateId: "01HGV2K8P9XYZ",
          name: "Diwali Campaign",
          templateData: {
            subject: "Your Diwali Gift Awaits!",
            messageTitle: "Happy Diwali!",
            genericMessage: "Celebrate this festival with special rewards",
            imageUrls: [
              "https://thumbs.dreamstime.com/b/idyllic-summer-landscape-clear-mountain-lake-alps-45054687.jpg",
            ],
          },
        },
        {
          templateId: "01HGV2K8P9ABC",
          name: "Birthday Special",
          templateData: {
            subject: "Happy Birthday!",
            messageTitle: "Birthday Surprise",
            genericMessage: "We have a special gift for your birthday",
            imageUrls: [
              "https://images.pexels.com/photos/462162/pexels-photo-462162.jpeg",
            ],
          },
        },
        {
          templateId: "01HGV2K8P9DEF",
          name: "Anniversary Celebration",
          templateData: {
            subject: "Celebrating Together!",
            messageTitle: "Anniversary Special",
            genericMessage:
              "Thank you for being with us. Here's a special gift!",
            imageUrls: [
              "https://images.pexels.com/photos/462162/pexels-photo-462162.jpeg",
              "https://cdn.example.com/anniversary2.jpg",
            ],
          },
        },
        {
          templateId: "01HGV2K8P9GHI",
          name: "Welcome Gift",
          templateData: {
            subject: "Welcome to Our Family!",
            messageTitle: "Welcome Aboard",
            genericMessage:
              "We're excited to have you with us. Enjoy your welcome gift!",
            imageUrls: [
              "https://images.pexels.com/photos/462162/pexels-photo-462162.jpeg",
            ],
          },
        },
      ],
    };
  }

  async sendCommsBatch(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<SendCommsBatchResponse> {
    await defaultMockDelayPromise(1000);

    const queuedCount = Math.floor(Math.random() * 1000) + 500;
    const channels = 3; // Mock 3 channels

    return {
      jobId: generateMockJobId(),
      batchId,
      queuedCount,
      totalMessages: queuedCount * channels,
      status: "QUEUED",
    };
  }

  async getCommsBatchStatus(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsBatchStatusResponse> {
    await defaultMockDelayPromise(400);

    const totalAssets = Math.floor(Math.random() * 1000) + 100;
    const mapped = Math.floor(totalAssets * 0.95); // 95% mapped

    const sent = Math.floor(mapped * 0.75);
    const partial = Math.floor(mapped * 0.15);
    const failed = Math.floor(mapped * 0.05);
    const pending = mapped - sent - partial - failed;

    return {
      batchId,
      status: "PROCESSING",
      totalAssets,
      mapped,
      overallStats: {
        sent,
        partial,
        failed,
        pending,
      },
      channelStats: {
        EMAIL: {
          pending: Math.floor(pending * 0.3),
          sent: Math.floor(sent * 0.9),
          failed: Math.floor(failed * 0.2),
        },
        WHATSAPP: {
          pending: Math.floor(pending * 0.4),
          sent: Math.floor(sent * 0.85),
          failed: Math.floor(failed * 0.5),
        },
        SMS: {
          pending: Math.floor(pending * 0.3),
          sent: Math.floor(sent * 0.8),
          failed: Math.floor(failed * 0.3),
        },
      },
    };
  }

  async getBatchById(
    batchId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<BatchDetailsResponse> {
    await defaultMockDelayPromise(400);

    return {
      batchId,
      sourceReferenceId: `ORDER_${Math.random().toString(36).substr(2, 9).toUpperCase()}`,
      assetType: "VOUCHER",
      totalAssets: 50,
      channels: ["EMAIL", "WHATSAPP"],
      status: "DRAFT",
      messageTemplate: {
        subject: "Your Gift Card is Here!",
        companyLogoUrl:
          "https://assets.myhubble.money/web-assets/hubble_full.svg",
        messageTitle: "Thank you for your hard work",
        genericMessage:
          "We appreciate your dedication and effort. Please enjoy this gift card as a token of our appreciation.",
        imageUrl:
          "https://assets.myhubble.money/web-assets/comms/template1.png",
        senderDetails: {
          name: "John Doe",
          designation: "Manager",
        },
      },
    };
  }

  async resetCommsBatch(
    batchId: string,
    body: ResetBatchRequest
  ): Promise<ResetBatchResponse> {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    console.log(
      `Mock: Resetting batch ${batchId} with options:`,
      body.resetOptions
    );

    // Mock successful reset response
    return {
      status: 200,
      message: `Batch reset completed. Reset options: ${body.resetOptions.join(", ")}`,
    };
  }

  async getCommsEntryByAsset(
    assetId: string,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsEntryByAssetResponse> {
    await defaultMockDelayPromise(600);

    const names = [
      "John Doe",
      "Jane Smith",
      "Robert Johnson",
      "Emily Davis",
      "Michael Wilson",
    ];
    const emails = [
      "john.doe@example.com",
      "jane.smith@example.com",
      "robert.johnson@example.com",
      "emily.davis@example.com",
      "michael.wilson@example.com",
    ];
    const phones = [
      "9876543210",
      "9876543211",
      "9876543212",
      "9876543213",
      "9876543214",
    ];
    const messages = [
      "Happy Diwali!",
      "Congratulations on your achievement!",
      "Thank you for your service!",
      "Enjoy your reward!",
      "Best wishes for the future!",
    ];

    const brands = ["Amazon", "Flipkart", "Swiggy", "Zomato", "Myntra"];
    const amounts = [500, 1000, 1500, 2000, 250];

    // Generate mock data based on assetId hash
    const hash = assetId
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const index = hash % names.length;
    const brand = brands[index % brands.length];

    const channels: CommunicationChannel[] = ["EMAIL", "SMS", "WHATSAPP"];
    const channelStatus: Record<CommunicationChannel, any> = {} as any;

    channels.forEach((channel) => {
      channelStatus[channel] = {
        status: "SENT",
        error: null,
        attemptedAt: new Date(
          Date.now() - Math.random() * 3600000
        ).toISOString(),
      };
    });

    const messageTitles = [
      "Happy Diwali!",
      "Congratulations!",
      "Thank You!",
      "Enjoy Your Reward!",
      "Best Wishes!",
    ];

    const genericMessages = [
      "Celebrate this festival with special rewards",
      "In recognition of your outstanding performance and dedication, we are delighted to name you Employee of the Month.",
      "We appreciate your dedication and effort. Please enjoy this gift card as a token of our appreciation.",
      "Keep up the excellent work!",
      "Wishing you continued success!",
    ];

    const senderNames = [
      "Rajat Singh",
      "Priya Sharma",
      "John Doe",
      "Sarah Wilson",
      "Amit Kumar",
    ];

    const designations = [
      "SVP HR, Acme",
      "Manager",
      "Director",
      "Team Lead",
      "VP Operations",
    ];

    return {
      entryId: generateMockEntryId(),
      assetId,
      displayId: `BATCH-${Math.random().toString(36).substr(2, 4).toUpperCase()}-${String(index + 1).padStart(5, "0")}`,
      sequenceNumber: index + 1,
      metadata: {
        type: "VOUCHER",
        amount: amounts[index % amounts.length],
        brandName: brand,
        brandLogoUrl: `https://app.myhubble.money/static/brand-assets/${brand.toLowerCase()}/top_brand.png`,
        tncUrl: `save.dev.hbbl.in/r/${Math.random().toString(36).substr(2, 10)}`,
        buttonUrl: `/${brand.toLowerCase()}-gift-card`,
      },
      recipientName: names[index],
      recipientEmail: emails[index],
      recipientPhone: phones[index],
      personalizedMessage: messages[index],
      deliveryStatus: "SENT",
      channelStatus,
      sentAt: new Date(Date.now() - Math.random() * 86400000).toISOString(),
      messageTitle: messageTitles[index % messageTitles.length],
      genericMessage: genericMessages[index % genericMessages.length],
      senderDetails: {
        name: senderNames[index % senderNames.length],
        designation: designations[index % designations.length],
      },
    };
  }

  async getCommsBatchesList(
    params?: CommsBatchesListParams,
    headers?: Record<string, string>,
    options?: FetchOptions
  ): Promise<CommsBatchesListResponse> {
    await defaultMockDelayPromise(800);

    const pageNo = params?.pageNo || 0;
    const limit = Math.min(params?.limit || 20, 100);
    const statusFilter = params?.status;

    // Generate mock batches
    const allBatches = [
      {
        batchId: generateMockBatchId(),
        sourceReferenceId: "DIWALI_CAMPAIGN_2024",
        assetType: "VOUCHER" as const,
        totalAssets: 1000,
        mapped: 1000,
        channels: ["EMAIL", "WHATSAPP"] as CommunicationChannel[],
        status: "DRAFT" as const,
        // status: "COMPLETED" as const,
      },
      {
        batchId: generateMockBatchId(),
        sourceReferenceId: "EMPLOYEE_REWARDS_Q4",
        assetType: "VOUCHER" as const,
        totalAssets: 500,
        mapped: 450,
        channels: ["EMAIL", "SMS"] as CommunicationChannel[],
        status: "DRAFT" as const,
      },
      {
        batchId: generateMockBatchId(),
        sourceReferenceId: "BIRTHDAY_WISHES_NOV",
        assetType: "VOUCHER" as const,
        totalAssets: 250,
        mapped: 250,
        channels: ["EMAIL", "WHATSAPP", "SMS"] as CommunicationChannel[],
        status: "DRAFT" as const,
      },
    ];

    // Filter by status if provided
    const filteredBatches = statusFilter
      ? allBatches.filter((batch) => batch.status === statusFilter)
      : allBatches;

    // Paginate
    const startIndex = pageNo * limit;
    const endIndex = startIndex + limit;
    const paginatedBatches = filteredBatches.slice(startIndex, endIndex);

    return {
      data: paginatedBatches,
      cursor: {
        pageNo,
        hasNext: endIndex < filteredBatches.length,
      },
    };
  }
}
